import React, { useRef } from "react";
import { Toolbar as RhombusToolbar } from "rhombus-react";
import { QR_DESIGN_DETAILS } from "forms/list";
import { DotSettingsSection } from "routes/qrcodes/qr_design/DotSettingsSection";
import { initialize, reduxForm, submit } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import { QrCodeDetailsSection } from "routes/qrcodes/qr_design/QrCodeDetailsSection";
import { QrCodesDesignFormFooter } from "routes/qrcodes/qr_design/QrCodesDesignFormFooter";
import { QrCodeDesignPreview } from "routes/qrcodes/qr_design/QrCodeDesignPreview";
import { translateFromDesignDataIntoQrDesignFormData } from "routes/qrcodes/qr_design/utils/QrDesignToFormDataTranslator";
import { QrDesigns } from "reducers/QrDesigns";
import { defaultQrDesignValues } from "./models/QrDesignFormDefaultValues";
import { getQrCodeDesignConfiguration, qrCodeDesignConfiguration } from "selectors/QrCodes";
import { push } from "connected-react-router";
import ProgressContainer from "widgets/ProgressContainer";
import { hasPermission } from "utils/common";
import { buildNotificationMessage } from "reducers/Notifications";
import QRCodeStyling from "@innovid/innocode-styling";

import "./QrCodesDesignForm.css";

const domain = "Creative View";
const action = "Upload Assets";
const QR_SCANNABILITY_ERROR_PAYLOAD = {
	type: "error",
	dismissIn: 10000,
	messageHeader: "QR Code Scannability Validation Failed",
	message: "The QR Code might not be 100% scannable, please adjust your design parameters and try again, or contact support if the issue persists.",
};

const submitQrDesignForm = (formValues, dispatch, props) => {
	const qrCodeDesignConfigurationData = getQrCodeDesignConfiguration(formValues);
	let actionToDispatch = QrDesigns.submitQrDesign(props.libraryId, qrCodeDesignConfigurationData);
	if (formValues.isEdition) {
		actionToDispatch = QrDesigns.submitEditionQrDesign(props.designId, qrCodeDesignConfigurationData);
	}
	const redirectAction = push(`/library/${props.libraryId}/qr-designs/`);
	return dispatch(actionToDispatch).then((response) => {
		if (response.success) {
			return dispatch(redirectAction);
		}
	});
};

export default function QrCodesDesignForm(props) {
	const dispatch = useDispatch();
	const user = useSelector(state => state.you);
	const qrDesignDetails = useSelector(state => qrCodeDesignConfiguration(state)?.details);
	const { current: qrCode } = useRef(new QRCodeStyling(qrDesignDetails));
	const hasPermissionToAction = hasPermission(user, domain, action);
	const qrCodesDesignState = useSelector(state => state.qrDesigns.design);
	const isLoadingQrDesign = useSelector(state => state.qrDesigns.isLoadingQrDesign);
	const isReadOnly = (!!qrCodesDesignState?.numQrCodes && qrCodesDesignState.numQrCodes > 0) || !hasPermissionToAction;
	const retrieveInitialQrDesignValues = async () => {
		const resp = await dispatch(QrDesigns.getQrDesign(props.libraryId, props.designId));
		dispatch(initialize(
			QR_DESIGN_DETAILS,
			{
				...resp.success ? translateFromDesignDataIntoQrDesignFormData(resp.payload.data) : defaultQrDesignValues,
				isEdition: resp.success,
			}
		));
	};
	const handleSubmitForm = (event) => {
		event.preventDefault();
		qrCode?.scan().then((isScannable) => {
			if (isScannable){
				dispatch(submit(QR_DESIGN_DETAILS));
			} else {
				dispatch(buildNotificationMessage(QR_SCANNABILITY_ERROR_PAYLOAD));
			}
		});
	};
	React.useEffect(() => {
		retrieveInitialQrDesignValues();
	}, []);
	return (
		<>
			<RhombusToolbar className="qr-codes-design-toolbar">
				<h3>New QR Code Design</h3>
			</RhombusToolbar>
			<ProgressContainer isLoading={isLoadingQrDesign || props.submitting}>
				<form name={QR_DESIGN_DETAILS} className="qr-codes-design-container" onSubmit={handleSubmitForm}>
					<div className="qr-codes-design-form">
						<QrCodeDetailsSection readonly={isReadOnly} />
						<DotSettingsSection readonly={isReadOnly} />
					</div>
					<QrCodeDesignPreview qrCode={qrCode} qrDesignDetails={qrDesignDetails} />
					<QrCodesDesignFormFooter readonly={isReadOnly} />
				</form>
			</ProgressContainer>
		</>
	);
}

QrCodesDesignForm.displayName = "QrCodesDesignForm";
QrCodesDesignForm = reduxForm({
	form: QR_DESIGN_DETAILS,
	onSubmit: submitQrDesignForm,
})(QrCodesDesignForm);
